import React, { useEffect, useRef, useState } from "react";
import { LogIn, Mail, ShieldCheck } from "lucide-react";
import { apiUrl } from "../utils/api";

interface AuthenticatedUser {
  id: string;
  email: string;
  display_name: string;
}

interface GoogleIdentity {
  accounts: {
    id: {
      initialize: (options: { client_id: string; callback: (response: { credential: string }) => void }) => void;
      renderButton: (element: HTMLElement, options: { theme: string; size: string; width: number }) => void;
    };
    oauth2: {
      initTokenClient: (options: { client_id: string; scope: string; callback: (response: { access_token?: string; error?: string }) => void }) => { requestAccessToken: () => void };
    };
  };
}

let googleInitializedClientId: string | null = null;

declare global {
  interface Window {
    google?: GoogleIdentity;
  }
}

export const AuthPanel: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [googleReady, setGoogleReady] = useState(Boolean(window.google));
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

  useEffect(() => {
    let cancelled = false;
    fetch(apiUrl("/api/auth/me"), { credentials: "include" })
      .then(async (response) => {
        if (response.status === 401) return;
        if (!response.ok) throw new Error(`Session lookup failed with status ${response.status}.`);
        const payload = await response.json() as { authenticated: boolean; user?: AuthenticatedUser };
        if (!cancelled && payload.authenticated && payload.user) setUser(payload.user);
      })
      .catch((error: unknown) => {
        if (!cancelled) setMessage(error instanceof Error ? error.message : "Account status could not be loaded.");
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!clientId || user) return;

    let cancelled = false;
    let initializationStarted = false;
    let pollTimer: number | undefined;
    let timeoutTimer: number | undefined;

    const initializeGoogleButton = () => {
      if (cancelled || initializationStarted || !window.google || !googleButtonRef.current) return;
      initializationStarted = true;
      if (pollTimer !== undefined) window.clearInterval(pollTimer);
      if (googleInitializedClientId === clientId) {
        window.google.accounts.id.renderButton(googleButtonRef.current, { theme: "filled_black", size: "large", width: 280 });
        setGoogleReady(true);
        return;
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            const response = await fetch(apiUrl("/api/auth/google"), {
            method: "POST",
            headers: { "Content-Type": "application/json", "X-NEX-Referral-Code": window.localStorage.getItem("nex_referral_code") ?? "" },
            credentials: "include",
            body: JSON.stringify({ id_token: credential }),
            });
            if (!response.ok) {
            const detail = await response.json().catch(() => null) as { detail?: string } | null;
            setMessage(detail?.detail ?? "Google sign-in could not be completed.");
            return;
          }
          const payload = await response.json() as { user: AuthenticatedUser };
            setUser(payload.user);
            window.dispatchEvent(new Event("nex:auth-state-changed"));
            setMessage("");
          } catch (error: unknown) {
            setMessage(error instanceof Error ? error.message : "Google sign-in could not reach the account service.");
          }
        },
      });
      googleButtonRef.current.replaceChildren();
      googleInitializedClientId = clientId;
      window.google.accounts.id.renderButton(googleButtonRef.current, { theme: "filled_black", size: "large", width: 280 });
      setGoogleReady(true);
    };

    const googleScript = document.getElementById("google-identity-services") as HTMLScriptElement | null;
    if (window.google) {
      initializeGoogleButton();
    } else if (googleScript) {
      googleScript.addEventListener("load", initializeGoogleButton, { once: true });
      googleScript.addEventListener("error", () => {
        if (!cancelled) setMessage("Google sign-in could not be loaded. Check network access to accounts.google.com.");
      }, { once: true });
      timeoutTimer = window.setTimeout(() => {
        if (!cancelled && !window.google) setMessage("Google sign-in is taking too long to load.");
      }, 10000);
    } else {
      setMessage("Google sign-in is unavailable because its client script is missing.");
    }

    pollTimer = window.setInterval(() => {
      if (window.google) {
        window.clearInterval(pollTimer);
        initializeGoogleButton();
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearInterval(pollTimer);
      if (timeoutTimer !== undefined) window.clearTimeout(timeoutTimer);
      googleScript?.removeEventListener("load", initializeGoogleButton);
    };
  }, [clientId]);

  const requestMagicLink = async (event: React.FormEvent) => {
    event.preventDefault();
    const response = await fetch(apiUrl("/api/auth/magic-link"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email }),
    });
    setMessage(response.ok ? "Check your email for a sign-in link." : "Email sign-in is not available yet.");
  };

  const signOut = async () => {
    const response = await fetch(apiUrl("/api/auth/logout"), { method: "POST", credentials: "include" });
    if (!response.ok) {
      setMessage("Sign out could not be completed.");
      return;
    }
    setUser(null);
    window.dispatchEvent(new Event("nex:auth-state-changed"));
    setMessage("");
  };

  return (
    <div className={`pointer-events-auto rounded-2xl border border-cyan-400/25 bg-slate-950/95 p-4 shadow-2xl backdrop-blur-md ${compact ? "w-full" : "w-80"}`} style={{ fontFamily: "'Times New Roman', Times, serif" }}>
      {user ? (
        <>
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-base font-semibold text-cyan-200">
                <ShieldCheck className="h-4 w-4 text-emerald-300" />
                <span>Your observatory</span>
              </div>
              <p className="mt-1 text-xs text-slate-400">Signed in as {user.display_name || user.email}</p>
              <p className="mt-1 break-all text-[11px] text-slate-500">{user.email}</p>
            </div>
            <button type="button" onClick={signOut} className="rounded-lg border border-slate-700 px-2 py-1 text-[11px] text-slate-300 hover:border-cyan-400/50 hover:text-white">Sign out</button>
          </div>
          <div className="mt-3 border-t border-slate-800 pt-3 text-[11px] text-emerald-300">Your observatory is saved to this account.</div>
        </>
      ) : (
        <>
          <div className="mb-1 flex items-center gap-2 text-base font-semibold text-cyan-200">
            <ShieldCheck className="h-4 w-4 text-cyan-300" />
            <span>Save your observatory</span>
          </div>
          <p className="mb-3 pl-6 text-xs leading-5 text-slate-400">Keep your chart and preferences with your account.</p>
          <div ref={googleButtonRef} className="flex min-h-10 justify-center" />
          {!clientId && <p className="text-xs text-amber-300">Google sign-in is not configured. Set VITE_GOOGLE_CLIENT_ID and restart Vite.</p>}
          {clientId && !googleReady && !message && <p className="text-xs text-slate-500">Loading Google sign-in…</p>}
          <form onSubmit={requestMagicLink} className="mt-3 flex gap-2">
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" required placeholder="Email sign-in" className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-900 px-2 py-2 text-xs text-white outline-none focus:border-cyan-400" />
            <button type="submit" aria-label="Send email sign-in link" className="rounded-lg border border-slate-700 px-2 text-cyan-300 hover:border-cyan-400"><Mail className="h-4 w-4" /></button>
          </form>
          {message && <p className="mt-2 text-xs text-rose-300">{message}</p>}
          <p className="mt-3 flex items-center gap-1 text-[10px] text-slate-500"><LogIn className="h-3 w-3" /> Sign in to save your observatory.</p>
        </>
      )}
    </div>
  )
};

